import { useEffect, useState } from "react";
import { IoClose } from "react-icons/io5";
import { getAgreementList } from "../../../../services/AppService";
import { useTranslation } from "react-i18next";

const AddWorkOrderForm = ({ onSubmit, editData, onClose, open }) => {
    const {t}=useTranslation();
    const [formData, setFormData] = useState({
        tenderProcessId: "",
        negotiationId: "",
        agreementId: "",
        workOrderNo: "",
        workOrderDate: "",
        workStartDate: "",
        workEndDate: "",
        duration: ""
    });

    const [displayFields, setDisplayFields] = useState({
        workTitle: "",
        tenderDate: "",
        tenderValue: "",
        bidderName: "",
        aboveBelow: "",
        bidAmount: ""
    });

    const [agreementList, setAgreementList] = useState([]);

    useEffect(() => {
        if (open) fetchAgreements();
    }, [open]);

    const fetchAgreements = async () => {
        try {
            // Agreements provide the link to both Tender and Negotiation
            const res = await getAgreementList("", 0, 1000);
            setAgreementList(res.content || []);
        } catch (err) {
            console.error("Failed to fetch agreements", err);
        }
    };

    // Auto-calculate Duration based on start/end dates
    useEffect(() => {
        if (formData.workStartDate && formData.workEndDate) {
            const start = new Date(formData.workStartDate);
            const end = new Date(formData.workEndDate);
            const diffTime = Math.abs(end - start);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            setFormData(prev => ({ ...prev, duration: diffDays }));
        }
    }, [formData.workStartDate, formData.workEndDate]);

    // Handle Edit vs Add mapping
    useEffect(() => {
        if (editData && open) {
            setFormData({
                tenderProcessId: editData.tenderProcessId || "",
                negotiationId: editData.negotiationId || "",
                agreementId: Number(editData.agreementId) || "",
                workOrderNo: editData.workOrderNo || "",
                workOrderDate: editData.workOrderDate || "",
                workStartDate: editData.workStartDate || "",
                workEndDate: editData.workEndDate || "",
                duration: editData.duration || ""
            });
            setDisplayFields({
                workTitle: editData.workTitle || "",
                tenderDate: editData.tenderDate || "",
                tenderValue: editData.tenderValue || "",
                bidderName: editData.bidderName || "",
                aboveBelow: editData.aboveBelow || "",
                bidAmount: editData.bidAmount || ""
            });
        } else if (open) {
            resetForm();
        }
    }, [editData, open]);

    const handleAgreementChange = (e) => {
        const agrId = e.target.value;
        setFormData(prev => ({ ...prev, agreementId: agrId }));

        if (agrId) {
            const selected = agreementList.find(a => a.id === Number(agrId));
            if (selected) {
                setDisplayFields({
                    workTitle: selected.workTitle || "",
                    tenderDate: selected.tenderDate || "",
                    tenderValue: selected.tenderValue || "",
                    bidderName: selected.bidderName || "",
                    aboveBelow: selected.aboveBelow || "",
                    bidAmount: selected.bidAmount || ""
                });
                setFormData(prev => ({
                    ...prev,
                    agreementId: agrId,
                    tenderProcessId: selected.tenderProcessId,
                    negotiationId: selected.negotiationId
                }));
            }
        }
    };

    const resetForm = () => {
        setFormData({ tenderProcessId: "", negotiationId: "", agreementId: "", workOrderNo: "", workOrderDate: "", workStartDate: "", workEndDate: "", duration: "" });
        setDisplayFields({ workTitle: "", tenderDate: "", tenderValue: "", bidderName: "", aboveBelow: "", bidAmount: "" });
    };

    const handleFieldChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    if (!open) return null;

    return (
        <div className="staff-modal-overlay">
            <div className="staff-modal" style={{ width: '1100px', maxWidth: '95%' }}>
                <div className="staff-modal-header">
                    <div className="staff-modal-title">{t("pageTitles.workOrderList")}</div>
                    <button className="admin-modal-close" onClick={onClose}><IoClose /></button>
                </div>
                <div className="staff-form-body">
                    <form onSubmit={(e) => { e.preventDefault(); onSubmit(formData); }}>
                        
                        {/* Section 1: Search Tender (Auto-filled) */}
                        <fieldset className="border p-4 rounded-md mb-4">
                            <legend className="text-sm font-bold px-2">{t("forms.workOrder.searchTender")}</legend>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="form-group">
                                    <label className="staff-modal-label">{t("forms.workOrder.tenderId")} <span>*</span></label>
                                    <select name="agreementId" value={formData.agreementId} onChange={handleAgreementChange} className="staff-modal-input" required>
                                        <option value="">Select Tender Id</option>
                                        {agreementList.map(a => <option key={a.id} value={a.id}>{a.tenderId}</option>)}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label className="staff-modal-label">{t("forms.workOrder.tenderDate")}</label>
                                    <input value={displayFields.tenderDate} className="staff-modal-input bg-gray-100" readOnly />
                                </div>
                                <div className="form-group md:col-span-2">
                                    <label className="staff-modal-label">{t("forms.workOrder.workTitle")}</label>
                                    <input value={displayFields.workTitle} className="staff-modal-input bg-gray-100" readOnly />
                                </div>
                            </div>
                        </fieldset>

                        {/* Section 2: Contractor (Read-only) */}
                        <fieldset className="border p-4 rounded-md mb-4">
                            <legend className="text-sm font-bold px-2">{t("forms.workOrder.contractor")}</legend>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="form-group md:col-span-2">
                                    <label className="staff-modal-label">{t("forms.workOrder.bidderName")}</label>
                                    <input value={displayFields.bidderName} className="staff-modal-input bg-gray-100" readOnly />
                                </div>
                                <div className="form-group">
                                    <label className="staff-modal-label">{t("forms.workOrder.bidAmount")}</label>
                                    <input value={displayFields.bidAmount} className="staff-modal-input bg-gray-100" readOnly />
                                </div>
                                 <div className="form-group">
                                    <label className="staff-modal-label">{t("forms.workOrder.aboveBelow")}</label>
                                    <input value={displayFields.aboveBelow ? `${displayFields.aboveBelow}%` : ""} className="staff-modal-input bg-gray-100" placeholder="Above / Below %" readOnly />
                                </div>
                            </div>
                        </fieldset>

                        {/* Section 3: Work Order Details (Inputs) */}
                        <fieldset className="border p-4 rounded-md mb-4 border-blue-200">
                            <legend className="text-sm font-bold px-2">{t("forms.workOrder.orderInfo")}</legend>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="form-group">
                                    <label className="staff-modal-label">{t("forms.workOrder.workOrderNo")}<span>*</span></label>
                                    <input name="workOrderNo" value={formData.workOrderNo} onChange={handleFieldChange} className="staff-modal-input" required />
                                </div>
                                <div className="form-group">
                                    <label className="staff-modal-label">{t("common.date")} <span>*</span></label>
                                    <input type="date" name="workOrderDate" value={formData.workOrderDate} onChange={handleFieldChange} className="staff-modal-input" required />
                                </div>
                                <div className="form-group">
                                    <label className="staff-modal-label">{t("forms.workOrder.startDate")} <span>*</span></label>
                                    <input type="date" name="workStartDate" value={formData.workStartDate} onChange={handleFieldChange} className="staff-modal-input" required />
                                </div>
                                <div className="form-group">
                                    <label className="staff-modal-label">{t("forms.workOrder.endDate")} <span>*</span></label>
                                    <input type="date" name="workEndDate" value={formData.workEndDate} onChange={handleFieldChange} className="staff-modal-input" required />
                                </div>
                                <div className="form-group">
                                    <label className="staff-modal-label">{t("forms.workOrder.duration")}</label>
                                    <input value={formData.duration} className="staff-modal-input bg-gray-50" readOnly placeholder="Auto-calculated" />
                                </div>
                            </div>
                        </fieldset>

                        <div className="flex justify-center mt-6">
                            <button type="submit" className="staff-btn-save px-12">{t("common.save")}</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AddWorkOrderForm;